import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-hot-toast";
import { Smartphone } from "lucide-react";
import { Button } from "../common/Button";
import { call, callableMessage } from "../../services/callables";

/** settings/global.directUpi, client view (server re-validates everything). */
export interface DirectUpiSettings { enabled: boolean; vpa: string; payeeName: string }
export function directUpiFromSettings(raw: any): DirectUpiSettings | null {
  if (!raw || raw.enabled !== true) return null;
  const vpa = typeof raw.vpa === "string" ? raw.vpa.trim() : "";
  const payeeName = typeof raw.payeeName === "string" ? raw.payeeName.trim() : "";
  return /^[^\s@]+@[^\s@]+$/.test(vpa) && payeeName ? { enabled: true, vpa, payeeName } : null;
}

const SAFE_UPI_PAY = /^upi:\/\/pay\?[A-Za-z0-9._~%&=-]+$/;

interface Started { ref: string; amount: number; link: string; vpa: string; payeeName: string }

/** Backup payment without PayU: upi://pay link (mobile) / QR (desktop), then the customer submits the UTR. */
export const DirectUpiPanel: React.FC<{
  amount: number;
  isDesktop: boolean;
  onBack: () => void;
  onDone: () => void;
}> = ({ amount, isDesktop, onBack, onDone }) => {
  const [st, setSt] = useState<Started | null>(null);
  const [busy, setBusy] = useState(false);
  const [utr, setUtr] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const d: any = await call("directUpiStart", { amount });
      if (!d || !SAFE_UPI_PAY.test(d.link)) throw new Error("Bad UPI link");
      setSt(d);
      if (!isDesktop) window.location.href = d.link;
    } catch (e: any) {
      toast.error(callableMessage(e, "Couldn't start the UPI payment"));
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!st) return;
    const clean = utr.replace(/[\s-]/g, "");
    if (!/^[A-Za-z0-9]{10,35}$/.test(clean)) return toast.error("Enter the 12-digit UTR / UPI reference number");
    setBusy(true);
    try {
      await call("directUpiSubmitUtr", { ref: st.ref, utr: clean });
      setSubmitted(true);
    } catch (e: any) {
      toast.error(callableMessage(e, "Couldn't submit the UTR"));
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="text-center space-y-4">
        <div className="rounded-lg bg-green-50 text-green-800 text-sm p-3">
          Thanks! We'll check the payment and add ₹{amount.toFixed(2)} to your wallet once it's confirmed
          (ref {st?.ref}).
        </div>
        <Button onClick={onDone} className="w-full">Done</Button>
      </div>
    );
  }

  return (
    <div className="text-center space-y-4">
      <div className="text-3xl font-bold text-gray-900">₹{amount.toFixed(2)}</div>
      {!st ? (
        <>
          <p className="text-xs text-gray-600">
            Pay directly by UPI (not through PayU). It isn't confirmed automatically: after paying, enter the
            UTR / reference number and we'll add the money once we've checked it.
          </p>
          <Button onClick={start} isLoading={busy} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700">
            <Smartphone size={18} className="mr-2" /> {isDesktop ? "Show UPI QR code" : "Pay directly by UPI"}
          </Button>
        </>
      ) : (
        <>
          <div className="text-xs text-gray-600">to <b>{st.payeeName}</b> ({st.vpa})<br />Note / ref: <b>{st.ref}</b></div>
          {isDesktop ? (
            <div className="flex justify-center"><QRCodeSVG value={st.link} size={200} /></div>
          ) : (
            <button type="button" onClick={() => { window.location.href = st.link; }}
              className="w-full py-2 rounded-lg border border-indigo-300 text-sm font-semibold text-indigo-700">
              Open UPI app again
            </button>
          )}
          {isDesktop && <p className="text-[11px] text-gray-500">Scan with any UPI app on your phone.</p>}
          <div className="text-left">
            <label className="text-xs font-semibold text-gray-700">After paying, enter the UTR / UPI reference no.</label>
            <input value={utr} onChange={(e) => setUtr(e.target.value)} inputMode="numeric" maxLength={40}
              placeholder="12-digit UTR" className="w-full border rounded-md px-3 py-2 mt-1 outline-none" />
          </div>
          <Button onClick={submit} isLoading={busy} disabled={!utr} className="w-full">I've paid – submit</Button>
        </>
      )}
      <button type="button" onClick={onBack} className="w-full text-xs text-gray-600 underline">Back</button>
    </div>
  );
};
